import { useCallback, useEffect, useMemo, useState } from 'react';
import { Plus, Edit2, Trash2, Info, X, Search } from 'lucide-react';
import toast from 'react-hot-toast';
import { Modal } from '../components/Modal';
import { ColorField } from '../components/ColorField';
import { carouselService } from '../services/carouselService';
import { productService } from '../services/productService';
import { fieldClass, labelClass } from '../utils/form';
import { rowNumber } from '../utils/rowNumber';
import type { Product } from '../types';

type CarouselProduct = {
  productId: string;
  name: string;
  image: string;
  sortOrder: number;
  isExcluded: boolean;
};

type CarouselRow = {
  id: string;
  slug: string;
  title: string;
  subtitle: string;
  sourceType: 'manual' | 'newest' | 'bestselling';
  variant: 'deal' | 'plain' | 'highlight' | 'bestsellers';
  placement: 'before_promos' | 'after_promos';
  bgFrom: string;
  bgVia: string;
  bgTo: string;
  accentColor: string;
  ctaText: string;
  ctaLink: string;
  productLimit: number;
  order: number;
  isActive: boolean;
  startsAt: string | null;
  endsAt: string | null;
  countdownEndsAt: string | null;
  products: CarouselProduct[];
};

type FormState = Omit<CarouselRow, 'id' | 'products'> & {
  products: CarouselProduct[];
};

const SOURCE_LABELS: Record<FormState['sourceType'], string> = {
  manual: 'دستی (انتخاب محصولات)',
  newest: 'خودکار — تازه‌ها',
  bestselling: 'خودکار — پرفروش',
};

const VARIANT_LABELS: Record<FormState['variant'], string> = {
  deal: 'شگفت‌انگیز (تیره + تایمر)',
  plain: 'ساده',
  highlight: 'هایلایت (گرادیان)',
  bestsellers: 'پرفروش با تب',
};

const PLACEMENT_LABELS: Record<FormState['placement'], string> = {
  before_promos: 'قبل از پروموها',
  after_promos: 'بعد از پروموها',
};

function num(value: unknown, fallback = 0): number {
  const n = Number(value);
  return Number.isFinite(n) ? n : fallback;
}

function toLocalInput(iso: string | null | undefined): string {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

function fromLocalInput(value: string): string | null {
  if (!value.trim()) return null;
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return null;
  return d.toISOString();
}

function emptyForm(): FormState {
  return {
    slug: '',
    title: '',
    subtitle: '',
    sourceType: 'manual',
    variant: 'plain',
    placement: 'after_promos',
    bgFrom: '',
    bgVia: '',
    bgTo: '',
    accentColor: '',
    ctaText: 'مشاهده همه',
    ctaLink: '/products',
    productLimit: 10,
    order: 50,
    isActive: true,
    startsAt: null,
    endsAt: null,
    countdownEndsAt: null,
    products: [],
  };
}

function mapProduct(raw: Record<string, unknown>): CarouselProduct {
  return {
    productId: String(raw.productId ?? raw.id ?? ''),
    name: String(raw.name ?? ''),
    image: String(raw.image ?? ''),
    sortOrder: num(raw.sortOrder),
    isExcluded: Boolean(raw.isExcluded),
  };
}

function mapRow(raw: Record<string, unknown>): CarouselRow {
  const products = Array.isArray(raw.products)
    ? (raw.products as Record<string, unknown>[]).map(mapProduct)
    : [];
  const source = String(raw.sourceType ?? 'manual');
  const variant = String(raw.variant ?? 'plain');
  const placement = String(raw.placement ?? 'after_promos');
  return {
    id: String(raw.id ?? ''),
    slug: String(raw.slug ?? ''),
    title: String(raw.title ?? ''),
    subtitle: String(raw.subtitle ?? ''),
    sourceType: (['manual', 'newest', 'bestselling'].includes(source)
      ? source
      : 'manual') as FormState['sourceType'],
    variant: (['deal', 'plain', 'highlight', 'bestsellers'].includes(variant)
      ? variant
      : 'plain') as FormState['variant'],
    placement: (placement === 'before_promos' ? 'before_promos' : 'after_promos'),
    bgFrom: String(raw.bgFrom ?? ''),
    bgVia: String(raw.bgVia ?? ''),
    bgTo: String(raw.bgTo ?? ''),
    accentColor: String(raw.accentColor ?? ''),
    ctaText: String(raw.ctaText ?? 'مشاهده همه'),
    ctaLink: String(raw.ctaLink ?? '/products'),
    productLimit: num(raw.productLimit, 10),
    order: num(raw.order ?? raw.sortOrder),
    isActive: Boolean(raw.isActive),
    startsAt: raw.startsAt ? String(raw.startsAt) : null,
    endsAt: raw.endsAt ? String(raw.endsAt) : null,
    countdownEndsAt: raw.countdownEndsAt ? String(raw.countdownEndsAt) : null,
    products,
  };
}

function formFromRow(row: CarouselRow): FormState {
  const { id: _id, ...rest } = row;
  return { ...rest, products: [...row.products] };
}

function payloadFromForm(form: FormState): Record<string, unknown> {
  return {
    slug: form.slug.trim(),
    title: form.title.trim(),
    subtitle: form.subtitle.trim(),
    sourceType: form.sourceType,
    variant: form.variant,
    placement: form.placement,
    bgFrom: form.bgFrom.trim(),
    bgVia: form.bgVia.trim(),
    bgTo: form.bgTo.trim(),
    accentColor: form.accentColor.trim(),
    ctaText: form.ctaText.trim() || 'مشاهده همه',
    ctaLink: form.ctaLink.trim() || '/products',
    productLimit: num(form.productLimit, 10),
    order: num(form.order),
    sortOrder: num(form.order),
    isActive: form.isActive,
    startsAt: form.startsAt,
    endsAt: form.endsAt,
    countdownEndsAt: form.countdownEndsAt,
    products: form.products.map((p, i) => ({
      productId: p.productId,
      sortOrder: p.sortOrder || i,
      isExcluded: p.isExcluded,
    })),
  };
}

export function CarouselsPage() {
  const [items, setItems] = useState<CarouselRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState<CarouselRow | null>(null);
  const [form, setForm] = useState<FormState>(emptyForm);
  const [catalog, setCatalog] = useState<Product[]>([]);
  const [productQuery, setProductQuery] = useState('');

  const isAuto = form.sourceType === 'newest' || form.sourceType === 'bestselling';

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const data = await carouselService.list();
      setItems(data.map(mapRow).sort((a, b) => a.order - b.order));
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'خطا در دریافت کروسل‌ها');
      setItems([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  useEffect(() => {
    if (!formOpen) return;
    void productService
      .getProducts({ page: 1, limit: 100 })
      .then((res) => setCatalog(res.data ?? []))
      .catch(() => setCatalog([]));
  }, [formOpen]);

  const filteredCatalog = useMemo(() => {
    const q = productQuery.trim();
    const selected = new Set(form.products.map((p) => p.productId));
    return catalog
      .filter((p) => !selected.has(String(p.id)))
      .filter((p) => !q || p.name?.includes(q) || p.sku?.includes(q))
      .slice(0, 20);
  }, [catalog, form.products, productQuery]);

  const closeForm = () => {
    setFormOpen(false);
    setEditing(null);
    setForm(emptyForm());
    setProductQuery('');
  };

  const openCreate = () => {
    setEditing(null);
    setForm(emptyForm());
    setFormOpen(true);
  };

  const openEdit = (row: CarouselRow) => {
    setEditing(row);
    setForm(formFromRow(row));
    setFormOpen(true);
  };

  const handleSave = async () => {
    if (!form.title.trim()) {
      toast.error('عنوان الزامی است');
      return;
    }
    setSaving(true);
    try {
      const payload = payloadFromForm(form);
      if (editing) {
        await carouselService.update(editing.id, payload);
        toast.success('کروسل به‌روزرسانی شد');
      } else {
        await carouselService.create(payload);
        toast.success('کروسل ایجاد شد');
      }
      closeForm();
      await load();
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'ذخیره ناموفق بود');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (row: CarouselRow) => {
    if (!window.confirm(`کروسل «${row.title}» حذف شود؟`)) return;
    try {
      await carouselService.remove(row.id);
      toast.success('حذف شد');
      await load();
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'حذف ناموفق بود');
    }
  };

  const setField = <K extends keyof FormState>(key: K, value: FormState[K]) => {
    setForm((prev) => ({ ...prev, [key]: value }));
  };

  const addProduct = (p: Product) => {
    const id = String(p.id);
    if (form.products.some((x) => x.productId === id)) return;
    const image = p.mainImage?.thumbnailUrl || p.mainImage?.url || p.images?.[0]?.url || '';
    setForm((prev) => ({
      ...prev,
      products: [
        ...prev.products,
        {
          productId: id,
          name: p.name,
          image,
          sortOrder: prev.products.length,
          isExcluded: isAuto,
        },
      ],
    }));
  };

  const removeProduct = (productId: string) => {
    setForm((prev) => ({
      ...prev,
      products: prev.products.filter((p) => p.productId !== productId),
    }));
  };

  const moveProduct = (productId: string, dir: -1 | 1) => {
    setForm((prev) => {
      const idx = prev.products.findIndex((p) => p.productId === productId);
      if (idx < 0) return prev;
      const next = [...prev.products];
      const j = idx + dir;
      if (j < 0 || j >= next.length) return prev;
      [next[idx], next[j]] = [next[j], next[idx]];
      return {
        ...prev,
        products: next.map((p, i) => ({ ...p, sortOrder: i })),
      };
    });
  };

  return (
    <div className="space-y-6" dir="rtl">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="page-title text-xl font-bold text-slate-900 sm:text-3xl">کروسل‌های صفحه اصلی</h1>
          <p className="text-slate-600 mt-1">اولویت، بازه نمایش، محصولات و استایل هر بخش</p>
        </div>
        <button
          type="button"
          onClick={openCreate}
          className="w-full sm:w-auto flex items-center gap-2 bg-indigo-600 text-white px-4 py-2 rounded-lg hover:bg-indigo-700 transition-colors font-medium"
        >
          <Plus className="h-4 w-4" />
          افزودن کروسل
        </button>
      </div>

      <div className="rounded-xl border border-sky-200 bg-sky-50 p-4 text-sm text-sky-950">
        <div className="flex gap-2">
          <Info className="h-5 w-5 flex-none text-sky-600 mt-0.5" />
          <ul className="list-disc pr-5 space-y-1 leading-7 text-sky-900/90">
            <li>
              <strong>بازه نمایش:</strong> از / تا — اگر خالی باشد همیشه (در صورت فعال بودن) نشان داده می‌شود.
            </li>
            <li>
              <strong>تایمر شگفت‌انگیز:</strong> فقط برای استایل deal؛ اختیاری است — خالی = تا پایان امروز.
            </li>
            <li>
              <strong>منبع دستی:</strong> محصولات را خودتان اضافه کنید. <strong>تازه/پرفروش:</strong> محتوا
              خودکار است؛ محصولات انتخاب‌شده از لیست حذف می‌شوند.
            </li>
            <li>
              <strong>تب پرفروش:</strong> با variant «پرفروش با تب» تب‌های دسته‌بندی حفظ می‌شود.
            </li>
          </ul>
        </div>
      </div>

      {loading ? (
        <div className="rounded-lg border border-slate-200 bg-white p-10 text-center text-slate-500">
          در حال بارگذاری...
        </div>
      ) : items.length === 0 ? (
        <div className="rounded-lg border border-dashed border-slate-300 bg-white p-10 text-center text-slate-500">
          هنوز کروسلی ثبت نشده.
        </div>
      ) : (
        <div className="overflow-hidden rounded-xl border border-slate-200 bg-white">
          <div className="admin-table-scroll">
            <table className="min-w-full divide-y divide-slate-200 text-sm">
            <thead className="bg-slate-50 text-slate-600">
              <tr>
                <th className="px-4 py-3 text-right font-medium">ردیف</th>
                <th className="px-4 py-3 text-right font-medium">کروسل</th>
                <th className="px-4 py-3 text-right font-medium">منبع</th>
                <th className="px-4 py-3 text-right font-medium">جایگاه</th>
                <th className="px-4 py-3 text-right font-medium">ترتیب</th>
                <th className="px-4 py-3 text-right font-medium">وضعیت</th>
                <th className="px-4 py-3 text-right font-medium">عملیات</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {items.map((row, index) => (
                <tr key={row.id} className="hover:bg-slate-50/80">
                  <td className="px-4 py-3 text-sm tabular-nums text-slate-500">{rowNumber(index)}</td>
                  <td className="px-4 py-3">
                    <div className="font-semibold text-slate-900">{row.title}</div>
                    <div className="text-xs text-slate-500 mt-0.5">
                      {VARIANT_LABELS[row.variant]} · {row.products.length} محصول
                    </div>
                  </td>
                  <td className="px-4 py-3 text-slate-600">{SOURCE_LABELS[row.sourceType]}</td>
                  <td className="px-4 py-3 text-slate-600">{PLACEMENT_LABELS[row.placement]}</td>
                  <td className="px-4 py-3 text-slate-600">{row.order}</td>
                  <td className="px-4 py-3">
                    <span
                      className={`rounded-full px-2.5 py-1 text-xs font-medium ${
                        row.isActive ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-600'
                      }`}
                    >
                      {row.isActive ? 'فعال' : 'غیرفعال'}
                    </span>
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-2">
                      <button
                        type="button"
                        onClick={() => openEdit(row)}
                        className="rounded-lg border border-slate-200 p-2 text-blue-600 hover:bg-blue-50"
                        title="ویرایش"
                      >
                        <Edit2 className="h-4 w-4" />
                      </button>
                      <button
                        type="button"
                        onClick={() => void handleDelete(row)}
                        className="rounded-lg border border-slate-200 p-2 text-red-600 hover:bg-red-50"
                        title="حذف"
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          </div>
        </div>
      )}

      <Modal
        isOpen={formOpen}
        onClose={closeForm}
        title={editing ? 'ویرایش کروسل' : 'افزودن کروسل'}
        size="xl"
        footer={
          <>
            <button
              type="button"
              onClick={closeForm}
              disabled={saving}
              className="px-4 py-2 rounded-lg border border-slate-300 text-slate-700 hover:bg-slate-50 disabled:opacity-50"
            >
              انصراف
            </button>
            <button
              type="button"
              onClick={() => void handleSave()}
              disabled={saving}
              className="px-4 py-2 rounded-lg bg-indigo-600 text-white hover:bg-indigo-700 disabled:opacity-50"
            >
              {saving ? 'در حال ذخیره...' : 'ذخیره'}
            </button>
          </>
        }
      >
        <div className="space-y-4 max-h-[70vh] overflow-y-auto pe-1" dir="rtl">
          <div className="grid gap-4 sm:grid-cols-2">
            <div>
              <label className={labelClass}>عنوان</label>
              <input className={fieldClass} value={form.title} onChange={(e) => setField('title', e.target.value)} />
            </div>
            <div>
              <label className={labelClass}>اسلاگ (اختیاری)</label>
              <input
                className={fieldClass}
                value={form.slug}
                onChange={(e) => setField('slug', e.target.value)}
                dir="ltr"
                placeholder="deals"
              />
            </div>
          </div>
          <div>
            <label className={labelClass}>زیرعنوان</label>
            <input
              className={fieldClass}
              value={form.subtitle}
              onChange={(e) => setField('subtitle', e.target.value)}
            />
          </div>

          <div className="grid gap-4 sm:grid-cols-3">
            <div>
              <label className={labelClass}>منبع محصولات</label>
              <select
                className={fieldClass}
                value={form.sourceType}
                onChange={(e) => {
                  const sourceType = e.target.value as FormState['sourceType'];
                  const auto = sourceType === 'newest' || sourceType === 'bestselling';
                  setForm((prev) => ({
                    ...prev,
                    sourceType,
                    products: prev.products.map((p) => ({ ...p, isExcluded: auto })),
                  }));
                }}
              >
                {(Object.keys(SOURCE_LABELS) as FormState['sourceType'][]).map((k) => (
                  <option key={k} value={k}>
                    {SOURCE_LABELS[k]}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className={labelClass}>استایل نمایش</label>
              <select
                className={fieldClass}
                value={form.variant}
                onChange={(e) => setField('variant', e.target.value as FormState['variant'])}
              >
                {(Object.keys(VARIANT_LABELS) as FormState['variant'][]).map((k) => (
                  <option key={k} value={k}>
                    {VARIANT_LABELS[k]}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className={labelClass}>جایگاه</label>
              <select
                className={fieldClass}
                value={form.placement}
                onChange={(e) => setField('placement', e.target.value as FormState['placement'])}
              >
                {(Object.keys(PLACEMENT_LABELS) as FormState['placement'][]).map((k) => (
                  <option key={k} value={k}>
                    {PLACEMENT_LABELS[k]}
                  </option>
                ))}
              </select>
            </div>
          </div>

          <div className="grid gap-4 sm:grid-cols-2">
            <div>
              <label className={labelClass}>متن دکمه</label>
              <input
                className={fieldClass}
                value={form.ctaText}
                onChange={(e) => setField('ctaText', e.target.value)}
              />
            </div>
            <div>
              <label className={labelClass}>لینک دکمه</label>
              <input
                className={fieldClass}
                value={form.ctaLink}
                onChange={(e) => setField('ctaLink', e.target.value)}
                dir="ltr"
              />
            </div>
          </div>

          <div className="grid gap-4 sm:grid-cols-2">
            <div>
              <label className={labelClass}>حداکثر تعداد محصول</label>
              <input
                type="number"
                className={fieldClass}
                value={form.productLimit}
                onChange={(e) => setField('productLimit', num(e.target.value, 10))}
              />
            </div>
            <div>
              <label className={labelClass}>اولویت (عدد کمتر = بالاتر)</label>
              <input
                type="number"
                className={fieldClass}
                value={form.order}
                onChange={(e) => setField('order', num(e.target.value))}
              />
            </div>
          </div>

          <div className="rounded-lg border border-slate-200 bg-slate-50 p-3 space-y-3">
            <p className="text-sm font-semibold text-slate-800">بازه نمایش (اختیاری)</p>
            <div className="grid gap-4 sm:grid-cols-2">
              <div>
                <label className={labelClass}>از</label>
                <input
                  type="datetime-local"
                  className={fieldClass}
                  value={toLocalInput(form.startsAt)}
                  onChange={(e) => setField('startsAt', fromLocalInput(e.target.value))}
                />
              </div>
              <div>
                <label className={labelClass}>تا</label>
                <input
                  type="datetime-local"
                  className={fieldClass}
                  value={toLocalInput(form.endsAt)}
                  onChange={(e) => setField('endsAt', fromLocalInput(e.target.value))}
                />
              </div>
            </div>
            {form.variant === 'deal' ? (
              <div>
                <label className={labelClass}>پایان تایمر شگفت‌انگیز (اختیاری)</label>
                <input
                  type="datetime-local"
                  className={fieldClass}
                  value={toLocalInput(form.countdownEndsAt)}
                  onChange={(e) => setField('countdownEndsAt', fromLocalInput(e.target.value))}
                />
                <p className="mt-1 text-xs text-slate-500">خالی بگذارید تا تا پایان امروز شمارش شود.</p>
              </div>
            ) : null}
          </div>

          {(form.variant === 'deal' || form.variant === 'highlight') && (
            <div className="rounded-lg border border-slate-200 bg-slate-50 p-3 space-y-3">
              <p className="text-sm font-semibold text-slate-800">رنگ‌ها (اختیاری — خالی = پیش‌فرض)</p>
              <div className="grid gap-4 sm:grid-cols-2">
                <ColorField
                  label="گرادیان از"
                  value={form.bgFrom}
                  onChange={(v) => setField('bgFrom', v)}
                  placeholder="#052e16"
                />
                <ColorField
                  label="گرادیان میانی"
                  value={form.bgVia}
                  onChange={(v) => setField('bgVia', v)}
                  placeholder="#14532d"
                />
                <ColorField
                  label="گرادیان تا"
                  value={form.bgTo}
                  onChange={(v) => setField('bgTo', v)}
                  placeholder="#166534"
                />
                <ColorField
                  label="رنگ اکسنت"
                  value={form.accentColor}
                  onChange={(v) => setField('accentColor', v)}
                  placeholder="#fb7185"
                />
              </div>
            </div>
          )}

          <label className="flex items-center gap-2 text-sm text-slate-700">
            <input
              type="checkbox"
              checked={form.isActive}
              onChange={(e) => setField('isActive', e.target.checked)}
            />
            فعال (در سایت نمایش داده شود)
          </label>

          <div className="rounded-lg border border-slate-200 p-3 space-y-3">
            <div>
              <p className="text-sm font-semibold text-slate-800">
                {isAuto ? 'محصولات حذف‌شده از لیست خودکار' : 'محصولات کروسل'}
              </p>
              <p className="text-xs text-slate-500 mt-0.5">
                {isAuto
                  ? 'این محصولات در پرفروش/تازه‌ها نشان داده نمی‌شوند.'
                  : 'ترتیب با دکمه‌های بالا/پایین قابل تنظیم است.'}
              </p>
            </div>

            {form.products.length > 0 ? (
              <ul className="space-y-2">
                {form.products.map((p) => (
                  <li
                    key={p.productId}
                    className="flex items-center gap-2 rounded-lg border border-slate-100 bg-white px-2 py-1.5"
                  >
                    {p.image ? (
                      <img src={p.image} alt="" className="h-9 w-9 rounded object-cover" />
                    ) : (
                      <div className="h-9 w-9 rounded bg-slate-100" />
                    )}
                    <span className="flex-1 text-sm text-slate-800 line-clamp-1">{p.name || p.productId}</span>
                    {!isAuto ? (
                      <div className="flex gap-1">
                        <button
                          type="button"
                          className="rounded border px-2 text-xs"
                          onClick={() => moveProduct(p.productId, -1)}
                        >
                          ↑
                        </button>
                        <button
                          type="button"
                          className="rounded border px-2 text-xs"
                          onClick={() => moveProduct(p.productId, 1)}
                        >
                          ↓
                        </button>
                      </div>
                    ) : null}
                    <button
                      type="button"
                      onClick={() => removeProduct(p.productId)}
                      className="rounded p-1 text-red-600 hover:bg-red-50"
                      title="حذف"
                    >
                      <X className="h-4 w-4" />
                    </button>
                  </li>
                ))}
              </ul>
            ) : (
              <p className="text-xs text-slate-500">
                {isAuto ? 'هیچ محصولی حذف نشده.' : 'هنوز محصولی اضافه نشده.'}
              </p>
            )}

            <div className="relative">
              <Search className="pointer-events-none absolute right-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
              <input
                className={`${fieldClass} pr-9`}
                placeholder="جستجوی محصول برای افزودن..."
                value={productQuery}
                onChange={(e) => setProductQuery(e.target.value)}
              />
            </div>
            {filteredCatalog.length > 0 ? (
              <ul className="max-h-40 overflow-y-auto divide-y rounded-lg border border-slate-100">
                {filteredCatalog.map((p) => (
                  <li key={p.id}>
                    <button
                      type="button"
                      onClick={() => addProduct(p)}
                      className="flex w-full items-center gap-2 px-3 py-2 text-right text-sm hover:bg-slate-50"
                    >
                      <span className="flex-1 line-clamp-1">{p.name}</span>
                      <Plus className="h-4 w-4 text-indigo-600" />
                    </button>
                  </li>
                ))}
              </ul>
            ) : null}
          </div>
        </div>
      </Modal>
    </div>
  );
}
